import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Panel } from 'primereact/panel';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment-timezone';
import Paginator from '../../components/Paginator';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import DataTableHeader from './TransferTableHeader';

const messages = defineMessages({
  from: { id: 'transferrs.data_table.headers.transferFrom', defaultMessage: 'Transfer from' },
  to: { id: 'transferrs.data_table.headers.transferTo', defaultMessage: 'Transfer to' },
  amount: { id: 'transferrs.data_table.headers.transfered', defaultMessage: 'Transfered' },
  transferredBy: { id: 'transferrs.data_table.headers.transferedBy', defaultMessage: 'Transfered by' },
  reason: { id: 'transferrs.data_table.headers.transferReason', defaultMessage: 'Reason' },
  details: { id: 'transferrs.account_transfers.data_table.body.details', defaultMessage: 'Details' },
  transferedOn: {
    id: 'transferrs.account_consumption.data_table.headers.transferedOn',
    defaultMessage: 'Transfered on',
  },
  dataNotAvailable: {
    id: 'consumption.account_consumption.data_table.body.dataNotAvailable',
    defaultMessage: 'Data not available',
  },
});

const TransfersList = ({ currentAccount, connection, loading, locale }) => {
  const history = useHistory();
  const { location } = history;

  const { formatMessage, formatDate } = useIntl();

  const { edges, totalCount, indexFrom, indexTo, pageInfo } = connection;

  const rows = edges.map((edge) => {
    const { node } = edge;
    const { fromAccount, toAccount, transferredBy } = node;
    const by = transferredBy ? `${transferredBy.firstName} ${transferredBy.lastName}` : '';
    return {
      ...node,
      from: fromAccount ? `${fromAccount.displayName}` : formatMessage(messages.dataNotAvailable),
      transferredTo: toAccount ? `${toAccount.displayName}` : formatMessage(messages.dataNotAvailable),
      by,
    };
  });

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const fromHeader = formatMessage(messages.from);

  const toHeader = formatMessage(messages.to);
  const amountHeader = formatMessage(messages.amount);
  const transferredByHeader = formatMessage(messages.transferredBy);
  const transferReasonHeader = formatMessage(messages.reason);
  const transferedOnHeader = formatMessage(messages.transferedOn);

  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  const detailsTemplate = (rowData) => {
    const buttonLabel = formatMessage(messages.details);
    const url = `/accounts/${currentAccount}/transfers/${rowData.id}`;

    return <Button label={buttonLabel} className="p-button" onClick={() => history.push(url)} />;
  };

  function dateTemplate(rowData) {
    const transactionTime = formatDate(moment(rowData.transactionTime), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
    return transactionTime;
  }

  function transferedByTemplate(rowData) {
    const { by, transferredBy } = rowData;

    if (transferredBy) {
      return <Link to={`/accounts/${currentAccount}/users/${transferredBy.id}`}>{by}</Link>;
    }
    return <p>{formatMessage(messages.dataNotAvailable)}</p>;
  }

  function reasonTemplate(rowData) {
    const { transferReason } = rowData;

    if (transferReason.length > 20) {
      return `${transferReason.substring(0, 20)}...`;
    }

    return transferReason;
  }

  function amountTemplate(rowData) {
    const { amount } = rowData;

    const formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 });

    return formatter.format(amount);
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <DataTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={rows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={(event) => sortUrl(event, location, history)}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column field="from" header={fromHeader} />
                  <Column field="transferredTo" header={toHeader} sortable />
                  <Column field="transferredAmount" header={amountHeader} body={amountTemplate} sortable />
                  <Column field="by" header={transferredByHeader} body={transferedByTemplate} />
                  <Column field="transferReason" header={transferReasonHeader} body={reasonTemplate} />
                  <Column
                    field="transactionTime"
                    header={transferedOnHeader}
                    sortable
                    body={dateTemplate}
                    style={{ width: '110px' }}
                  />
                  <Column field="details" body={detailsTemplate} />
                </DataTable>
              </div>
              <div className="p-col-12 align-cener">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

TransfersList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

export default TransfersList;
